import { AssetStatus } from '@risksmart-app/domain/src/types/consts/asset-status';
import {
  Contributor_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledDatePicker from 'src/components/form/controlled-date-picker';
import { ControlledFileUpload } from 'src/components/form/controlled-file-upload/ControlledFileUpload';
import ControlledGroupAndUserContributorMultiSelect from 'src/components/form/controlled-group-and-user-contributor-multi-select';
import ControlledGroupAndUserSelect from 'src/components/form/controlled-group-and-user-select';
import ControlledInput from 'src/components/form/controlled-input';
import ControlledRadioGroup from 'src/components/form/controlled-radio-group';
import { noTransform } from 'src/components/form/controlled-radio-group/radioGroupUtils';
import ControlledTextarea from 'src/components/form/controlled-textarea';
import DepartmentSelector from 'src/components/form/department-selector';
import CustomisableFieldWrapper from 'src/components/form/form/customisable-form/CustomisableFieldWrapper';
import FieldGroup from 'src/components/form/form/customisable-form/FieldGroup';
import TagSelector from 'src/components/form/tag-selector';
import { useFormConfig } from 'src/utils/table/hooks/form/useFormConfig';

import { TestIds } from './AssetFormFieldsTestIds';
import type { AssetFormFieldData } from './assetSchema';

type Props = {
  readonly?: boolean;
};

const statusOptions = Object.values(AssetStatus).map((value) => ({
  value,
  label: value,
}));

const AssetFormFields: FC<Props> = ({ readonly }) => {
  const { control } = useFormContext<AssetFormFieldData>();

  const assetFormConfig = useFormConfig(Parent_Type_Enum.Asset);

  const { t: st } = useTranslation(['common'], { keyPrefix: 'assets' });
  const { t } = useTranslation(['common']);

  return (
    <CustomisableFieldWrapper readOnly={readonly}>
      <ControlledInput
        testId={TestIds.Title}
        forceRequired={true}
        key={'title'}
        name={assetFormConfig.Title.fieldId}
        label={assetFormConfig.Title.formLabel}
        control={control}
        placeholder={st('fields.Title_placeholder') ?? ''}
        description={st('fields.Title_help')}
        disabled={readonly}
      />

      <ControlledTextarea
        testId={TestIds.Description}
        key={'description'}
        name={assetFormConfig.Description.fieldId}
        label={assetFormConfig.Description.formLabel}
        placeholder={st('fields.Description_placeholder') ?? ''}
        control={control}
        description={st('fields.Description_help')}
        disabled={readonly}
      />

      <ControlledGroupAndUserContributorMultiSelect
        key={'owners'}
        testId={TestIds.Owners}
        forceRequired={true}
        control={control}
        inheritedContributorsName={'ancestorContributors'}
        contributorType={Contributor_Type_Enum.Owner}
        includeGroups={true}
        label={assetFormConfig.Owners.formLabel}
        name={assetFormConfig.Owners.fieldId}
        placeholder={t('fields.Owner_placeholder')}
        description={st('fields.Owner_help')}
        disabled={readonly}
      />

      <ControlledGroupAndUserContributorMultiSelect
        key={'contributors'}
        testId={TestIds.Contributors}
        control={control}
        contributorType={Contributor_Type_Enum.Contributor}
        includeGroups={true}
        inheritedContributorsName={'ancestorContributors'}
        label={assetFormConfig.Contributors.formLabel}
        name={assetFormConfig.Contributors.fieldId}
        placeholder={t('fields.Contributor_placeholder')}
        description={st('fields.Contributor_help')}
        disabled={readonly}
      />

      <FieldGroup key={'statusAndDates'}>
        <ControlledRadioGroup
          key={'status'}
          testId={TestIds.Status}
          forceRequired={true}
          label={assetFormConfig.Status.formLabel}
          description={st('fields.Status_help')}
          name={assetFormConfig.Status.fieldId}
          control={control}
          transform={noTransform}
          items={statusOptions}
          disabled={readonly}
        />

        <ControlledDatePicker
          key={'purchaseDate'}
          testId={TestIds.PurchaseDate}
          name={assetFormConfig.PurchaseDate.fieldId}
          label={assetFormConfig.PurchaseDate.formLabel}
          description={st('fields.PurchaseDate_help')}
          control={control}
          disabled={readonly}
        />
      </FieldGroup>

      <ControlledInput
        testId={TestIds.AssetType}
        key={'assetType'}
        name={assetFormConfig.AssetType.fieldId}
        label={assetFormConfig.AssetType.formLabel}
        control={control}
        placeholder={st('fields.AssetType_placeholder') ?? ''}
        description={st('fields.AssetType_help')}
        disabled={readonly}
      />

      <ControlledInput
        testId={TestIds.SerialNumber}
        key={'serialNumber'}
        name={assetFormConfig.SerialNumber.fieldId}
        label={assetFormConfig.SerialNumber.formLabel}
        control={control}
        placeholder={st('fields.SerialNumber_placeholder') ?? ''}
        description={st('fields.SerialNumber_help')}
        disabled={readonly}
      />

      <ControlledInput
        testId={TestIds.Location}
        key={'location'}
        name={assetFormConfig.Location.fieldId}
        label={assetFormConfig.Location.formLabel}
        control={control}
        placeholder={st('fields.Location_placeholder') ?? ''}
        description={st('fields.Location_help')}
        disabled={readonly}
      />

      <ControlledInput
        testId={TestIds.Cost}
        key={'cost'}
        type={'number'}
        name={assetFormConfig.Cost.fieldId}
        label={assetFormConfig.Cost.formLabel}
        control={control}
        placeholder={st('fields.Cost_placeholder') ?? ''}
        description={st('fields.Cost_help')}
        disabled={readonly}
      />

      <ControlledInput
        testId={TestIds.DepreciationMethod}
        key={'depreciationMethod'}
        name={assetFormConfig.DepreciationMethod.fieldId}
        label={assetFormConfig.DepreciationMethod.formLabel}
        control={control}
        placeholder={st('fields.DepreciationMethod_placeholder') ?? ''}
        description={st('fields.DepreciationMethod_help')}
        disabled={readonly}
      />

      <ControlledGroupAndUserSelect
        key={'assignee'}
        testId={TestIds.Assignee}
        control={control}
        includeGroups={false}
        label={assetFormConfig.Assignee.formLabel}
        name={assetFormConfig.Assignee.fieldId}
        description={st('fields.Assignee_help')}
        disabled={readonly}
      />

      <ControlledFileUpload
        key={'attachFiles'}
        testId={TestIds.AttachFiles}
        label={assetFormConfig.files.formLabel}
        description={t('fields.newFiles_help')}
        control={control}
        name={assetFormConfig.files.fieldId}
        disabled={readonly}
      />

      <TagSelector
        key={'tags'}
        name={assetFormConfig.tags.fieldId}
        label={assetFormConfig.tags.formLabel}
        testId={TestIds.Tags}
        control={control}
        disabled={readonly}
      />
      <DepartmentSelector
        key={'departments'}
        label={assetFormConfig.departments.formLabel}
        testId={TestIds.Departments}
        name={assetFormConfig.departments.fieldId}
        control={control}
        disabled={readonly}
      />
    </CustomisableFieldWrapper>
  );
};

export default AssetFormFields;
